import { DAILY_SALES_EARLIEST_DATE, DAILY_SALES_MAX_DAYS } from '@bonsai/shared';
import { useId, useState, type FormEvent } from 'react';

import type { SalesDateRange } from '../../../../api/daily-sales';
import { customRangeProblem } from '../daily-sales-range';

interface DailySalesCustomDatesProps {
  initial: SalesDateRange;
  today: string;
  onApply: (range: SalesDateRange) => void;
}

export function DailySalesCustomDates({ initial, today, onApply }: DailySalesCustomDatesProps) {
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [problem, setProblem] = useState<string | null>(null);
  const hintId = useId();
  const problemId = useId();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found = customRangeProblem({ from, to }, today);
    setProblem(found);
    if (!found) onApply({ from, to });
  };

  const describedBy = problem ? `${problemId} ${hintId}` : hintId;
  const field = (label: string, value: string, set: (v: string) => void) => (
    <label className="nb-ds-field">
      {label}
      <input
        type="date"
        value={value}
        min={DAILY_SALES_EARLIEST_DATE}
        max={today}
        aria-invalid={problem ? true : undefined}
        aria-describedby={describedBy}
        onChange={(e) => {
          set(e.target.value);
          setProblem(null);
        }}
      />
    </label>
  );

  return (
    <form className="nb-ds-custom" onSubmit={submit} noValidate>
      {field('From', from, setFrom)}
      {field('To', to, setTo)}
      <button type="submit" className="nb-btn nb-btn-primary">
        Show these dates
      </button>
      <p id={problemId} className="nb-ds-custom-problem" aria-live="polite">
        {problem}
      </p>
      <p id={hintId} className="nb-ds-meta nb-ds-custom-hint">
        Up to {DAILY_SALES_MAX_DAYS} days, ending today or earlier. Days are Eastern time.
      </p>
    </form>
  );
}
